import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { Upload, Download, RotateCcw, Lock, Share2, SlidersHorizontal } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import heroArt from "./bg-lfi.jpg";
import sample from "./sample.jpg";

const SLOGAN = "MERCI LFI";
const FONT = "Anton";

async function drawWatermark(canvas, img, opacity) {
  await document.fonts.load(`100px "${FONT}"`);
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  const diag = Math.hypot(w, h);
  ctx.font = `100px "${FONT}"`;
  const base = ctx.measureText(SLOGAN).width;
  const size = Math.min((100 * diag * 0.7) / base, Math.min(w, h) * 0.3);
  ctx.save();
  ctx.translate(w / 2, h / 2);
  ctx.rotate(-Math.atan2(h, w));
  ctx.font = `${size}px "${FONT}"`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.globalAlpha = opacity;
  ctx.fillStyle = "#E3001B";
  ctx.fillText(SLOGAN, 0, 0);
  ctx.restore();
}

const Dropzone = ({ onFile }) => {
  const [over, setOver] = useState(false);
  const pick = (files) => files?.[0]?.type.startsWith("image/") && onFile(files[0]);
  return (
    <label
      data-testid="upload-dropzone"
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); pick(e.dataTransfer.files); }}
      className={`dropzone ${over ? "is-over" : ""}`}
    >
      <input data-testid="upload-input" type="file" accept="image/*" className="hidden" onChange={(e) => pick(e.target.files)} />
      <Upload className="w-10 h-10 text-[#E3001B]" strokeWidth={1.5} />
      <span className="text-xl font-semibold mt-5">Déposez votre photo ici</span>
      <span className="text-sm text-neutral-400 mt-2">ou cliquez pour choisir un fichier — JPG, PNG, WEBP</span>
    </label>
  );
};

const SHARE_TEXT = "Merci LFI : le générateur officiel de gratitude républicaine";

const ShareLinks = () => {
  const url = encodeURIComponent(window.location.origin);
  const text = encodeURIComponent(SHARE_TEXT);
  return (
    <div data-testid="share-links" className="text-xs text-neutral-400 space-y-3">
      <p>Téléchargez la photo puis joignez-la à votre publication :</p>
      <div className="flex gap-3">
        <a data-testid="share-x-link" className="share-link" target="_blank" rel="noopener noreferrer" href={`https://x.com/intent/post?text=${text}&url=${url}`}>X</a>
        <a data-testid="share-facebook-link" className="share-link" target="_blank" rel="noopener noreferrer" href={`https://www.facebook.com/sharer/sharer.php?u=${url}`}>Facebook</a>
      </div>
    </div>
  );
};

const StepPreview = ({ opacity }) => {
  const ref = useRef(null);
  useEffect(() => {
    const i = new Image();
    i.onload = () => (opacity ? drawWatermark(ref.current, i, opacity) : Object.assign(ref.current, { width: i.naturalWidth, height: i.naturalHeight }).getContext("2d").drawImage(i, 0, 0));
    i.src = sample;
  }, [opacity]);
  return <canvas ref={ref} className="w-full h-auto block" />;
};

const STEPS = [
  { icon: Upload, title: "Déposez votre photo", text: "Glissez-la dans le cadre ou touchez-le pour la choisir.", opacity: 0 },
  { icon: SlidersHorizontal, title: "Réglez l'intensité", text: "Le curseur rend « MERCI LFI » plus discret ou plus visible.", opacity: 0.18 },
  { icon: Download, title: "Téléchargez ou partagez", text: "Récupérez la photo, ou envoyez-la directement sur vos réseaux.", opacity: 0.4 },
];

const HowItWorks = () => (
  <section data-testid="how-it-works" className="mt-20">
    <h2 className="display text-2xl sm:text-3xl mb-8">Comment ça marche ?</h2>
    <ol className="grid sm:grid-cols-3 gap-6">
      {STEPS.map(({ icon: Icon, title, text, opacity }, n) => (
        <li key={title} data-testid={`step-${n + 1}`} className="step-card fade-up" style={{ animationDelay: `${n * 0.12}s` }}>
          <StepPreview opacity={opacity} />
          <div className="p-5">
            <div className="flex items-center gap-3">
              <span className="step-num">{n + 1}</span>
              <Icon className="w-4 h-4 text-[#E3001B]" />
              <h3 className="font-semibold">{title}</h3>
            </div>
            <p className="text-sm text-neutral-400 mt-2">{text}</p>
          </div>
        </li>
      ))}
    </ol>
  </section>
);

const Editor = ({ file, onReset }) => {
  const canvasRef = useRef(null);
  const [img, setImg] = useState(null);
  const [opacity, setOpacity] = useState(0.25);
  const [showLinks, setShowLinks] = useState(false);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const i = new Image();
    i.onload = () => setImg(i);
    i.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const outRef = useRef(null);
  useEffect(() => {
    if (!img) return;
    const png = file.type === "image/png";
    const name = `${file.name.replace(/\.[^.]+$/, "")}-merci-lfi.${png ? "png" : "jpg"}`;
    drawWatermark(canvasRef.current, img, opacity).then(() =>
      canvasRef.current.toBlob((b) => { outRef.current = new File([b], name, { type: b.type }); }, png ? "image/png" : "image/jpeg", 0.92)
    );
  }, [img, opacity, file]);

  const canShareFile = () => outRef.current && navigator.canShare?.({ files: [outRef.current] });

  const download = () => {
    const f = outRef.current;
    if (!f) return;
    if (navigator.maxTouchPoints > 0 && canShareFile()) {
      navigator.share({ files: [f] }).catch(() => {});
      return;
    }
    const url = URL.createObjectURL(f);
    const a = document.createElement("a");
    a.href = url;
    a.download = f.name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const share = () => {
    if (canShareFile()) navigator.share({ files: [outRef.current], text: SHARE_TEXT }).catch(() => {});
    else setShowLinks(true);
  };

  return (
    <div className="fade-up grid lg:grid-cols-[1fr_300px] gap-10 items-start">
      <div className="canvas-frame">
        <canvas data-testid="watermark-canvas" ref={canvasRef} className="block max-w-full max-h-[55vh] lg:max-h-[70vh] mx-auto" />
      </div>
      <aside className="space-y-8">
        <div>
          <div className="flex justify-between text-sm mb-4">
            <span className="uppercase tracking-[0.2em] text-neutral-400">Intensité du filigrane</span>
            <span data-testid="opacity-value" className="font-semibold">{Math.round(opacity * 100)}%</span>
          </div>
          <Slider data-testid="opacity-slider" min={5} max={60} step={1} value={[opacity * 100]} onValueChange={([v]) => setOpacity(v / 100)} />
          <p className="text-xs text-neutral-400 mt-3">Plus c'est bas, plus le filigrane est discret.</p>
        </div>
        <button data-testid="download-btn" onClick={download} className="btn-primary w-full">
          <Download className="w-4 h-4" /> Télécharger la photo
        </button>
        <p data-testid="download-hint" className="text-xs text-neutral-400 -mt-5 lg:hidden">Sur téléphone, choisissez « Enregistrer l'image » pour la retrouver dans vos photos.</p>
        <button data-testid="share-btn" onClick={share} className="btn-ghost w-full">
          <Share2 className="w-4 h-4" /> Partager
        </button>
        {showLinks && <ShareLinks />}
        <button data-testid="reset-btn" onClick={onReset} className="btn-ghost w-full">
          <RotateCcw className="w-4 h-4" /> Changer de photo
        </button>
      </aside>
    </div>
  );
};

function App() {
  const [file, setFile] = useState(null);
  return (
    <>
      <main className="relative min-h-screen px-6 sm:px-12 lg:px-20 pt-6 pb-14 max-w-7xl mx-auto">
        <div className="hero-art fade-up">
          <img data-testid="hero-image" src={heroArt} alt="Logo La France Insoumise" />
        </div>
        <header className="mb-14 fade-up max-w-3xl">
          <h1 data-testid="page-title" className="display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            <span className="text-[#E3001B]">Merci LFI</span> : le générateur officiel de gratitude républicaine
          </h1>
          <p data-testid="page-subtitle" className="italic text-base md:text-lg text-neutral-200 mt-5">
            Parce que chaque vitrine brisée mérite sa dédicace !
          </p>
          <div data-testid="page-intro" className="space-y-4 text-base text-neutral-300 mt-8 max-w-2xl leading-relaxed">
            <p>Vous avez immortalisé une voiture en flammes, un abribus en miettes ou une rue transformée en champ de bataille ?</p>
            <p>Ne soyez pas ingrat. Derrière chaque nuit agitée se cache un travail d'ambiance patient, et il serait malpoli d'oublier de remercier ceux qui ont tant soufflé sur les braises.</p>
            <p>Ici, on rend à César ce qui est à César.</p>
            <p>En un clic, votre photo repart avec un hommage sobre et élégant, en diagonale, pour que personne ne rate le message. Avant de poster, pensez à dire merci.</p>
            <p className="font-semibold text-white">C'est la base !</p>
          </div>
        </header>
        {file ? <Editor file={file} onReset={() => setFile(null)} /> : <Dropzone onFile={setFile} />}
        <HowItWorks />
        <footer data-testid="privacy-note" className="mt-16 flex gap-2 items-start text-xs text-neutral-400 max-w-2xl">
          <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>Aucune image n'est stockée. Votre photo est traitée directement dans votre navigateur et disparaît dès que vous fermez la page. La gratitude, elle, reste.</span>
        </footer>
      </main>
    </>
  );
}

export default App;
